
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Type for inventory items
export type InventoryItem = {
  id: string;
  name: string;
  description: string | null;
  category: string;
  quantity: number;
  has_expiry: boolean;
  expiry_date: string | null;
  barcode: string | null;
  location: string | null;
  image_url: string | null;
  created_at: string;
  updated_at: string;
};

export const useInventoryItems = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchInventoryItems = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setItems(data || []);
    } catch (err: any) {
      console.error("Error fetching inventory items:", err.message);
      setError(err);
      toast.error("Failed to load inventory items", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
    
    // Set up real-time subscription for persona-specific tables and inventory_items
    const channels = [
      // Main inventory table channel
      supabase
        .channel('inventory-changes')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'inventory_items'
          }, 
          () => {
            fetchInventoryItems();
          }
        )
        .subscribe(),
        
      // Household inventory channel
      supabase
        .channel('household-changes')
        .on('postgres_changes', 
          {
            event: '*',
            schema: 'public',
            table: 'household_inventory'
          },
          () => {
            fetchInventoryItems();
          }
        )
        .subscribe(),
        
      // Restaurant inventory channel
      supabase
        .channel('restaurant-changes')
        .on('postgres_changes', 
          {
            event: '*',
            schema: 'public',
            table: 'restaurant_inventory'
          },
          () => {
            fetchInventoryItems();
          }
        )
        .subscribe(),
        
      // Retail inventory channel
      supabase
        .channel('retail-changes')
        .on('postgres_changes', 
          {
            event: '*',
            schema: 'public',
            table: 'retail_inventory'
          },
          () => {
            fetchInventoryItems();
          }
        )
        .subscribe(),
        
      // Healthcare inventory channel
      supabase
        .channel('healthcare-changes')
        .on('postgres_changes', 
          {
            event: '*',
            schema: 'public',
            table: 'healthcare_inventory'
          },
          () => {
            fetchInventoryItems();
          }
        )
        .subscribe()
    ];
      
    return () => {
      channels.forEach(channel => {
        supabase.removeChannel(channel);
      });
    };
  }, []);

  // Refresh items on demand
  const refreshItems = async () => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setItems(data || []);
      toast.success("Inventory items refreshed", {
        position: "bottom-right",
        dismissible: true,
      });
    } catch (err: any) {
      console.error("Error refreshing inventory items:", err.message);
      toast.error("Failed to refresh inventory items", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get recent items (can be used in dashboard overview)
  const getRecentItems = (limit: number = 5) => {
    return items.slice(0, limit);
  };

  return { items, isLoading, error, refreshItems, getRecentItems };
};
